<template>
  <div class="input">
    <label
      v-if="!internalLabel"
      class="input__label sr-only"
      :for="id">
      {{ label }}
    </label>
    <div class="input__container">
      <label
        v-if="internalLabel && modelValue"
        class="input__internal-label"
        :for="id">
        {{ placeholder }}
      </label>
      <input
        :id="id"
        :type="inputType"
        :autocomplete="autocomplete"
        :value="modelValue"
        @input="updateValue"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :class="[
          'input__field',
          { 'input__field--internal-label': internalLabel && modelValue },
        ]" />
      <svg
        v-if="icon"
        class="input__icon">
        <use :xlink:href="`#${icon}`" />
      </svg>
      <button
        v-if="type === 'password'"
        class="input__toggle-button"
        @click="togglePasswordVisibility"
        type="button">
        <svg class="input__icon-show">
          <use :xlink:href="`#${showPassword ? dontShow : show}`" />
        </svg>
      </button>
      <button
        v-if="hasDelete && type !== 'password'"
        class="input__delete-button"
        @click="clearInput"
        type="button">
        &times;
      </button>
    </div>
    <ul
      v-if="showSuggestions && filteredSuggestions.length"
      class="input__suggestions-list">
      <li
        v-for="(suggestion, index) in filteredSuggestions"
        :key="index"
        @mousedown.prevent="selectSuggestion(suggestion)"
        @keydown="handleSuggestionKeydown(suggestion, $event)"
        :class="{ 'input__suggestion--highlighted': index === selectedIndex }"
        class="input__suggestion"
        tabindex="0">
        <div class="input__suggestion-name">{{ suggestion.name }}</div>
        <div
          v-if="suggestion.district || suggestion.county"
          class="input__suggestion-location">
          <span v-if="suggestion.district">{{ suggestion.district }}</span>
          <span v-if="suggestion.district && suggestion.county">, </span>
          <span v-if="suggestion.county">{{ suggestion.county }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed } from 'vue'
  import show from '@/assets/icons/show.svg'
  import dontShow from '@/assets/icons/dontShow.svg'

  export default defineComponent({
    name: 'Input',
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: String,
        required: true,
      },
      type: {
        type: String,
        default: 'text',
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      autocomplete: {
        type: String,
        default: 'off',
      },
      suggestions: {
        type: Array as () => Array<{
          name: string
          code: string
          district?: string
          county?: string
        }>,
        default: () => [],
      },
      icon: {
        type: String,
        default: '',
      },
      hasDelete: {
        type: Boolean,
        default: false,
      },
      internalLabel: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'focus', 'blur', 'select-suggestion', 'clear'],
    setup(props, { emit }) {
      const showSuggestions = ref(false)
      const selectedIndex = ref(-1)
      const showPassword = ref(false)

      const inputType = computed(() => {
        return props.type === 'password' && showPassword.value ? 'text' : props.type
      })

      const filteredSuggestions = computed(() => {
        return props.suggestions.filter((suggestion) =>
          suggestion.name.toLowerCase().includes(props.modelValue.toLowerCase()),
        )
      })

      const updateValue = (event: Event) => {
        const target = event.target as HTMLInputElement
        emit('update:modelValue', target.value)
      }

      const handleFocus = (event: Event) => {
        showSuggestions.value = true
        emit('focus', event)
      }

      const handleBlur = (event: Event) => {
        emit('blur', event)
      }

      const selectSuggestion = (suggestion: {
        name: string
        code: string
        district?: string
        county?: string
      }) => {
        emit('update:modelValue', suggestion.name)
        emit('select-suggestion', suggestion.code)
        showSuggestions.value = false
      }

      const hideSuggestions = () => {
        setTimeout(() => {
          showSuggestions.value = false
        }, 200)
      }

      const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          selectedIndex.value = (selectedIndex.value + 1) % filteredSuggestions.value.length
        } else if (event.key === 'ArrowUp') {
          event.preventDefault()
          selectedIndex.value =
            (selectedIndex.value - 1 + filteredSuggestions.value.length) %
            filteredSuggestions.value.length
        } else if (event.key === 'Enter' && selectedIndex.value >= 0) {
          event.preventDefault()
          selectSuggestion(filteredSuggestions.value[selectedIndex.value])
        } else if (event.key === 'Escape') {
          showSuggestions.value = false
        }
      }

      const handleSuggestionKeydown = (
        suggestion: { name: string; code: string; district?: string; county?: string },
        event: KeyboardEvent,
      ) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          selectSuggestion(suggestion)
        }
      }

      const clearInput = () => {
        emit('update:modelValue', '')
        emit('clear')
      }

      const togglePasswordVisibility = () => {
        showPassword.value = !showPassword.value
      }

      return {
        showSuggestions,
        selectedIndex,
        showPassword,
        inputType,
        updateValue,
        handleFocus,
        handleBlur,
        selectSuggestion,
        hideSuggestions,
        handleKeydown,
        handleSuggestionKeydown,
        clearInput,
        togglePasswordVisibility,
        filteredSuggestions,
        show,
        dontShow,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .input {
    display: flex;
    flex-direction: column;
    position: relative;

    &__label {
      margin-bottom: rem(8);
      font-weight: bold;
    }

    &__internal-label {
      @include body-small;
      position: absolute;
      top: rem(10);
      left: 16px;
      color: $clr-heading;

      &:has(+ .input__field + .input__icon) {
        left: 40px;
      }
    }

    &__container {
      display: flex;
      align-items: center;
      position: relative;
      min-height: rem(64);
    }

    &__icon {
      position: absolute;
      left: 16px;
      width: 16px;
      height: 16px;
      fill: currentColor;
      color: $clr-secondary;
      pointer-events: none;
    }

    &__field {
      @include body-large;
      padding: rem(19) 16px;
      color: $clr-text;
      border: 1px solid $clr-primary-50;
      border-radius: 8px;
      background-color: $clr-background;
      flex: 1;
      box-shadow: $box-shadow-card;

      &:has(+ .input__icon) {
        min-height: 23.62px;
        padding-left: 40px;
      }

      &:active,
      &:focus-visible,
      &:focus {
        border-color: $clr-primary-700;
      }

      &:disabled {
        background-color: #f5f5f5;
        box-shadow: none;
      }

      @media (min-width: $breakpoint-xl) {
        width: 100%;

        &:has(+ .input__icon) {
          min-width: 220px;
        }
      }
    }

    &__field--internal-label {
      padding-bottom: 0.65rem;
      padding-top: 1.75rem;
    }

    &__delete-button,
    &__toggle-button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: $clr-primary-300;
      cursor: pointer;
      position: absolute;
      right: 1rem;
    }

    &__icon-show {
      width: 16px;
      height: 16px;
      color: $clr-primary-900;
    }

    &__suggestions-list {
      top: 55px;
      list-style: none;
      padding: 0;
      text-align: left;
      margin: 0;
      border: 1px solid $clr-primary-100;
      border-radius: 8px;
      max-height: 440px;
      overflow-y: auto;
      color: $clr-text;
      background-color: $clr-white;
      position: absolute;
      width: 100%;
      z-index: 1000;
    }

    &__suggestion {
      padding: 0.5rem;
      cursor: pointer;

      &:hover,
      &:focus,
      &--highlighted {
        background-color: $clr-primary-50;
        box-shadow: $box-shadow-hover;
      }

      &-name {
        font-weight: 500;
      }

      &-location {
        @include body-small;
        color: $clr-text-disable;
        margin-top: rem(2);
      }
    }
  }
</style>
